import type { AutomationType } from '@playbook-technologies/common-enums';
import * as Sentry from '@sentry/react';
import { isAxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import { makeAsync } from '@client/new/helpers/async';
import type { Async } from '@client/new/helpers/types';

import { AUTOMATION_ORDER } from '../automation-copy';
import {
  CreatorAutomationItem,
  getCreatorAutomations,
  updateCreatorAutomation,
} from '../automations.api';

export type AutomationsModel = {
  automations: CreatorAutomationItem[];
  pending: ReadonlySet<AutomationType>;
  errors: Partial<Record<AutomationType, AutomationsUpdateError>>;
  statuses: Partial<Record<AutomationType, AutomationsUpdateStatus>>;
  retry: () => Promise<void>;
  update: (automationType: AutomationType, enabled: boolean) => Promise<void>;
};

export type AutomationsRequestErrorKind =
  | 'unauthorized'
  | 'forbidden'
  | 'network';

export type AutomationsUpdateError = {
  kind: AutomationsRequestErrorKind;
  intendedEnabled: boolean;
};

export type AutomationsUpdateStatus = { kind: 'saving' | 'saved' };

export type AutomationsLoadError = {
  kind: AutomationsRequestErrorKind | 'contract';
  retry: () => Promise<void>;
};

const isKnownAutomationType = (value: string): value is AutomationType =>
  AUTOMATION_ORDER.some((type) => type === value);

const statusOf = (error: unknown): number | undefined =>
  isAxiosError(error) ? error.response?.status : undefined;

const errorKindOf = (error: unknown): AutomationsRequestErrorKind => {
  switch (statusOf(error)) {
    case 401:
      return 'unauthorized';
    case 403:
      return 'forbidden';
    default:
      return 'network';
  }
};

export const useAutomationsModel = (
  creatorId: number,
): Async<AutomationsModel, AutomationsLoadError> => {
  const [data, setData] = useState<
    Async<CreatorAutomationItem[], AutomationsLoadError>
  >(makeAsync.notFetched());
  const [pending, setPending] = useState<ReadonlySet<AutomationType>>(
    new Set(),
  );
  const pendingRef = useRef<ReadonlySet<AutomationType>>(new Set());
  const [errors, setErrors] = useState<
    Partial<Record<AutomationType, AutomationsUpdateError>>
  >({});
  const [statuses, setStatuses] = useState<
    Partial<Record<AutomationType, AutomationsUpdateStatus>>
  >({});

  const load = useCallback(async () => {
    setData(makeAsync.fetching());
    try {
      const response = await getCreatorAutomations(creatorId);
      const known = response.filter((item) => {
        const supported = isKnownAutomationType(item.automationType);
        if (!supported) {
          Sentry.captureMessage(
            `Unsupported creator automation type: ${item.automationType}`,
          );
        }
        return supported;
      });
      setData(
        known.length > 0
          ? makeAsync.fetched(known)
          : makeAsync.error({ kind: 'contract', retry: load }),
      );
    } catch (error) {
      setData(makeAsync.error({ kind: errorKindOf(error), retry: load }));
    }
  }, [creatorId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (data.type !== 'fetched') return data;

  const update: AutomationsModel['update'] = async (
    automationType,
    enabled,
  ) => {
    if (pendingRef.current.has(automationType)) return;
    const previous = data.value.find(
      (item) => item.automationType === automationType,
    );
    if (!previous) return;

    pendingRef.current = new Set(pendingRef.current).add(automationType);
    setPending(pendingRef.current);
    setStatuses((current) => ({
      ...current,
      [automationType]: { kind: 'saving' },
    }));
    setErrors((current) => ({ ...current, [automationType]: undefined }));
    setData((current) =>
      current.type === 'fetched'
        ? makeAsync.fetched(
            current.value.map((item) =>
              item.automationType === automationType
                ? { ...item, enabled }
                : item,
            ),
          )
        : current,
    );

    try {
      const canonical = await updateCreatorAutomation(
        creatorId,
        automationType,
        enabled,
      );
      setData((current) =>
        current.type === 'fetched'
          ? makeAsync.fetched(
              current.value.map((item) =>
                item.automationType === automationType ? canonical : item,
              ),
            )
          : current,
      );
      setStatuses((current) => ({
        ...current,
        [automationType]: { kind: 'saved' },
      }));
    } catch (error) {
      setData((current) =>
        current.type === 'fetched'
          ? makeAsync.fetched(
              current.value.map((item) =>
                item.automationType === automationType ? previous : item,
              ),
            )
          : current,
      );
      setErrors((current) => ({
        ...current,
        [automationType]: {
          kind: errorKindOf(error),
          intendedEnabled: enabled,
        },
      }));
      setStatuses((current) => ({ ...current, [automationType]: undefined }));
    } finally {
      setPending((current) => {
        const next = new Set(current);
        next.delete(automationType);
        pendingRef.current = next;
        return next;
      });
    }
  };

  return makeAsync.fetched({
    automations: data.value,
    pending,
    errors,
    statuses,
    retry: load,
    update,
  });
};
